"use client";

import { ReactNode, useCallback, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/navigation";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetTitle,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "ui";

import { AreaToggles } from "@/components/appearance/area-toggles";
import { ColorModeSelector } from "@/components/appearance/color-mode-selector";
import { FontSelector } from "@/components/appearance/font-selector";
import { HomeModuleToggles } from "@/components/appearance/home-module-toggles";
import { LayoutDiagram } from "@/components/appearance/layout-grid";
import { resetSidebarCollapseChoice } from "@/components/layouts/use-sidebar-collapse";
import { ADD_OR_REMOVE_AREAS_COMING_SOON } from "@/lib/customise-copy";
import { CUSTOMISE_DEFAULTS } from "@/lib/customise-defaults";
import { parseHomeModules } from "@/lib/home-modules";
import {
  DENSITY_OPTIONS,
  NAV_OPTIONS,
  POST_STYLE_OPTIONS,
  navDisabledReason,
} from "@/lib/layout-axes";
import { LAYOUT_OPTIONS, LayoutId } from "@/lib/layouts";
import { ADMIN_ROUTES } from "@/lib/routes";
import { useTheme } from "@/lib/state/theme";
import { THEME_PRESETS } from "@/lib/themes/presets";
import { trpc } from "@/lib/trpc/client";
import { cn } from "@/lib/utils";

/**
 * Customise your Studio — the whole arrangement in one sheet.
 *
 * The prototype's builder (`app/home/preview/builder-sheet.tsx`), wired to the
 * real stores. Reachable from every Customise affordance: the studio header,
 * and Console's sidebar footer. Appearance settings
 * (/admin/settings/appearance) remains the long form — this is the same
 * settings, where an owner is actually looking at the thing they are changing.
 *
 * TWO STORES, deliberately different save behaviour:
 *
 *   - Font, colour mode, theme, layout, navigation, post display, density and
 *     the feed modules are StudioPreference — every control the Appearance
 *     settings page offers, so the two surfaces cannot show a studio different
 *     sets of options. They preview locally through ThemeProvider and are
 *     written ONCE, when the sheet closes — the prototype's "members see this
 *     arrangement as soon as you close the sheet". Saving per click would mean
 *     a server round trip and a full router.refresh() on every toggle.
 *   - The navigation areas are StudioConfiguration, reached through the same
 *     `moduleConfig` procedures /admin/customise uses. AreaToggles saves each
 *     change immediately, as it does on the settings page. One Save writing two
 *     stores would leave the studio half-applied if either call failed.
 *
 * Only the header, density, post style and layout that a SERVER component
 * renders can show the result, so the save is followed by router.refresh() —
 * without it the arrangement is stored and the studio keeps rendering the
 * previous one until a hard reload.
 *
 * Permissions are the caller's: every trigger sits behind an OWNER/ADMIN
 * check. That check is not the control — `studio.updateAppearance` is a
 * studioOwnerProcedure and rejects a member who is not one, which is what the
 * error path below reports.
 */
export function CustomiseDrawer({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const {
    colorMode,
    themePreset,
    themeFont,
    layout,
    nav,
    postStyle,
    density,
    homeModules,
    hasUnsavedChanges,
    setLocalColorMode,
    setLocalFont,
    setLocalThemePreset,
    setLocalLayout,
    setLocalNav,
    setLocalPostStyle,
    setLocalDensity,
    setLocalHomeModules,
    resetToSaved,
    markAsSaved,
  } = useTheme();

  const router = useRouter();
  const utils = trpc.useUtils();
  const [isSaving, setIsSaving] = useState(false);

  const save = useCallback(async () => {
    setIsSaving(true);
    try {
      await utils.client.studio.updateAppearance.mutate({
        colorMode,
        themePreset,
        themeFont,
        layout,
        nav,
        postStyle,
        density,
        // Sent as an array; the procedure normalises and stores the string, so
        // the client cannot write an order the server would not accept.
        homeModules: parseHomeModules(homeModules),
      } as Parameters<typeof utils.client.studio.updateAppearance.mutate>[0]);
      markAsSaved();
      // The saver's own collapse override is dropped here: they have just set
      // how the studio is arranged and are looking straight at the result, so
      // a per-browser deviation stored earlier — under a nav mode or a layout
      // they have since changed — must not quietly outrank what they picked.
      resetSidebarCollapseChoice();
      router.refresh();
      toast.success("Appearance updated for all studio members");
    } catch {
      // Revert the preview rather than leaving the owner looking at an
      // arrangement no member will ever see.
      resetToSaved();
      toast.error("Could not save your arrangement. Nothing was changed.");
    } finally {
      setIsSaving(false);
    }
  }, [
    colorMode,
    themePreset,
    themeFont,
    layout,
    nav,
    postStyle,
    density,
    homeModules,
    markAsSaved,
    resetToSaved,
    router,
    utils,
  ]);

  /**
   * Closing IS the save — by Done, by Escape, by the scrim, or by the X. The
   * sheet closes straight away and the write runs behind it, so the sheet
   * never sits there blocking on a request; a failure reverts the preview and
   * says so.
   */
  const handleOpenChange = useCallback(
    (next: boolean) => {
      onOpenChange(next);
      if (next || !hasUnsavedChanges || isSaving) return;
      void save();
    },
    [hasUnsavedChanges, isSaving, onOpenChange, save],
  );

  /**
   * Back to what a studio created today would look like.
   *
   * Local only, like every other control here — it is committed when the sheet
   * closes, so a mis-click can still be changed back. The navigation areas are
   * deliberately untouched: they are a second store that saves immediately, so
   * a "reset" would silently rewrite the studio's navigation with no way back.
   */
  const handleReset = useCallback(() => {
    setLocalLayout(CUSTOMISE_DEFAULTS.layout);
    setLocalFont(CUSTOMISE_DEFAULTS.themeFont);
    setLocalColorMode(CUSTOMISE_DEFAULTS.colorMode);
    setLocalNav(CUSTOMISE_DEFAULTS.nav);
    setLocalPostStyle(CUSTOMISE_DEFAULTS.postStyle);
    setLocalDensity(CUSTOMISE_DEFAULTS.density);
    setLocalHomeModules(CUSTOMISE_DEFAULTS.homeModules);
    setLocalThemePreset(CUSTOMISE_DEFAULTS.themePreset);
  }, [
    setLocalColorMode,
    setLocalDensity,
    setLocalFont,
    setLocalHomeModules,
    setLocalLayout,
    setLocalNav,
    setLocalPostStyle,
    setLocalThemePreset,
  ]);

  return (
    <Sheet open={open} onOpenChange={handleOpenChange}>
      <SheetContent
        side="right"
        // p-0 and gap-0 undo the primitive's own padding: this sheet is a
        // header, a scrolling body and a footer, each with its own edge.
        className="flex w-full flex-col gap-0 p-0 sm:max-w-[460px]"
      >
        {/* pr-14 leaves the primitive's absolute close button its corner. */}
        <div className="flex shrink-0 flex-col gap-1 border-b border-border px-5 py-4 pr-14">
          <SheetTitle className="text-lg font-medium">
            Customise your Studio
          </SheetTitle>
          <SheetDescription>
            Members see this arrangement as soon as you close the sheet.
          </SheetDescription>
        </div>

        <div className="flex flex-1 flex-col gap-7 overflow-y-auto px-5 py-5">
          <Field label="Layout">
            <LayoutPicker value={layout} onChange={setLocalLayout} />
          </Field>

          <Field label="Top-level modules · these become your navigation">
            <AreaToggles />
            {/* Installing, removing and adding an area is not shipped yet.
                Kept visible-but-inert so the affordance stays discoverable and
                the explanation lands where an owner looks for it. aria-disabled
                rather than `disabled`: a disabled button swallows the pointer
                events the tooltip needs, and drops out of the tab order. */}
            <TooltipProvider delayDuration={0}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    aria-disabled="true"
                    onClick={(event) => event.preventDefault()}
                    className="inline-flex w-fit cursor-not-allowed items-center gap-1.5 rounded-lg border border-border px-3 py-2 text-xs font-medium text-muted-foreground opacity-60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  >
                    Add or remove areas
                    <ArrowUpRight className="h-3.5 w-3.5 shrink-0 opacity-60" />
                  </button>
                </TooltipTrigger>
                <TooltipContent side="top">
                  {ADD_OR_REMOVE_AREAS_COMING_SOON}
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </Field>

          <Field label="Feed modules · order them with the arrows">
            <HomeModuleToggles
              value={homeModules}
              onChange={setLocalHomeModules}
            />
          </Field>

          <Field label="Theme">
            <ThemePicker value={themePreset} onChange={setLocalThemePreset} />
          </Field>

          <Field label="Font">
            <FontSelector value={themeFont} onChange={setLocalFont} />
          </Field>

          <Field label="Colour mode">
            <ColorModeSelector value={colorMode} onChange={setLocalColorMode} />
          </Field>

          <Field label="Navigation">
            <Segmented
              label="Navigation"
              options={NAV_OPTIONS}
              value={nav}
              onChange={setLocalNav}
              // Reader has no nav column, so the axis does nothing there and
              // says so rather than offering a choice with no outcome.
              disabledReason={navDisabledReason(layout) ?? undefined}
            />
          </Field>

          <Field label="Post display">
            <Segmented
              label="Post display"
              options={POST_STYLE_OPTIONS}
              value={postStyle}
              onChange={setLocalPostStyle}
            />
          </Field>

          <Field label="Density">
            <Segmented
              label="Density"
              options={DENSITY_OPTIONS}
              value={density}
              onChange={setLocalDensity}
            />
          </Field>

          <p className="text-xs leading-relaxed text-muted-foreground">
            Members see this arrangement the moment you close the sheet. Areas
            apply as you switch them.
          </p>
        </div>

        <div className="flex shrink-0 items-center gap-3 border-t border-border px-5 py-3">
          <button
            type="button"
            onClick={handleReset}
            className="rounded text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            Reset to defaults
          </button>

          {/* The long form — fonts, colour mode and everything this sheet
              leaves out. The prototype's "copy link to this arrangement" has
              no counterpart here: a real studio's arrangement is stored, not
              carried in the query string. */}
          <Link
            href={ADMIN_ROUTES.settingsAppearance}
            className="ml-auto inline-flex items-center gap-1 rounded text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            All appearance settings
            <ArrowUpRight className="h-3 w-3 shrink-0 opacity-60" />
          </Link>

          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="inline-flex h-9 items-center rounded-lg bg-primary px-4 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
          >
            Done
          </button>
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return (
    <section className="flex flex-col gap-2.5">
      <h3 className="text-[11px] font-medium uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </h3>
      {children}
    </section>
  );
}

/**
 * The four layouts, two to a row.
 *
 * Not `LayoutGrid`: that one is `sm:grid-cols-2 lg:grid-cols-4`, and those
 * breakpoints are the VIEWPORT's, not this 460px sheet's — on a desktop it
 * would put four cards in a column narrow enough to break the names. The
 * thumbnails are the same drawing, imported rather than redrawn.
 */
function LayoutPicker({
  value,
  onChange,
}: {
  value: LayoutId;
  onChange: (layout: LayoutId) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Layout"
      className="grid grid-cols-2 gap-2"
    >
      {LAYOUT_OPTIONS.map((option) => {
        const isSelected = value === option.id;
        return (
          <button
            key={option.id}
            type="button"
            role="radio"
            aria-checked={isSelected}
            onClick={() => onChange(option.id)}
            className={cn(
              "flex flex-col gap-1.5 rounded-lg border p-2.5 text-left transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              isSelected
                ? "border-primary bg-primary/5"
                : "border-border bg-card hover:border-primary/40",
            )}
          >
            <span className="overflow-hidden rounded-md border border-border bg-background">
              <LayoutDiagram id={option.id} />
            </span>
            <span
              className={cn(
                "text-[13px] font-medium",
                isSelected ? "text-primary" : "text-foreground",
              )}
            >
              {option.name}
            </span>
            <span className="text-[11px] leading-snug text-muted-foreground">
              {option.description}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * The theme presets, three to a row.
 *
 * The settings page's ThemeGrid draws 48px swatches five across, which does
 * not fit here. Same presets, same two-tone swatch shorthand.
 */
function ThemePicker({
  value,
  onChange,
}: {
  value: string;
  onChange: (preset: string) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label="Studio theme"
      className="grid grid-cols-3 gap-2"
    >
      {THEME_PRESETS.map((preset) => {
        const isSelected = value === preset.key;
        return (
          <button
            key={preset.key}
            type="button"
            role="radio"
            aria-checked={isSelected}
            title={preset.description}
            onClick={() => onChange(preset.key)}
            className={cn(
              "flex items-center gap-2 rounded-lg border p-2 transition-colors",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
              isSelected
                ? "border-primary bg-primary/5"
                : "border-border bg-card hover:border-primary/40",
            )}
          >
            <span
              aria-hidden
              className="h-5 w-5 shrink-0 rounded-md border border-border"
              style={{
                background: `linear-gradient(135deg, ${preset.swatchColor} 0 55%, ${preset.swatchAccent} 55% 100%)`,
              }}
            />
            <span
              className={cn(
                "truncate text-xs font-medium",
                isSelected ? "text-primary" : "text-foreground",
              )}
            >
              {preset.name}
            </span>
          </button>
        );
      })}
    </div>
  );
}

/**
 * One row of exclusive options, for the axes with two or three of them.
 *
 * A radiogroup rather than `aria-pressed` buttons — genuinely exclusive
 * choices from a short list, which also gives arrow-key navigation for free.
 * The settings page's AxisSelector shows each option's description; there is
 * no room for that here, so the descriptions become the title attribute.
 */
function Segmented<T extends string>({
  label,
  options,
  value,
  onChange,
  disabledReason,
}: {
  label: string;
  options: readonly { id: T; name: string; description: string }[];
  value: T;
  onChange: (value: T) => void;
  disabledReason?: string;
}) {
  const isDisabled = Boolean(disabledReason);

  return (
    <div>
      <div
        role="radiogroup"
        aria-label={label}
        aria-disabled={isDisabled || undefined}
        className={cn(
          "flex gap-1 rounded-lg border border-border bg-card p-1",
          isDisabled && "opacity-50",
        )}
      >
        {options.map((option) => {
          const isSelected = value === option.id;
          return (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={isSelected}
              disabled={isDisabled}
              title={option.description}
              onClick={() => onChange(option.id)}
              className={cn(
                "h-8 flex-1 rounded-md text-xs font-medium transition-colors",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isDisabled && "cursor-not-allowed",
                isSelected
                  ? "bg-primary text-primary-foreground"
                  : "text-muted-foreground hover:text-foreground",
              )}
            >
              {option.name}
            </button>
          );
        })}
      </div>

      {disabledReason && (
        <p className="mt-2 text-xs text-muted-foreground">{disabledReason}</p>
      )}
    </div>
  );
}
